import * as React from 'react';
import classNames from 'classnames';

import Icon from 'components/icon';
import { useToast } from 'my-account/toast';
import { buildWizardUrl } from 'tempcontrol/utils/wizard-url';

interface ShareResultsWidgetProps {
  projectKey: string;
  className?: string;
  onRequestDownload: (email?: string) => void;
}

/**
 * Inline share widget from the tempcontrol s5 cutup. Copy URL follows the
 * SPB ShareModal clipboard + toast pattern; Send opens the download gate
 * (lead-capture modal) with the email field prefilled when provided.
 */
export const ShareResultsWidget: React.VFC<ShareResultsWidgetProps> = ({
  projectKey,
  className,
  onRequestDownload,
}) => {
  const toast = useToast();
  const [email, setEmail] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);
  const shareUrl = React.useMemo(() => buildWizardUrl(projectKey), [projectKey]);

  const handleCopyUrl = (event: React.MouseEvent) => {
    event.preventDefault();
    navigator.clipboard.writeText(shareUrl).then(() => {
      toast.notify({
        type: 'success',
        title: 'Success',
        message: 'The project link has been copied to your clipboard.',
      });
    });
  };

  const handleSend = (event: React.FormEvent) => {
    event.preventDefault();
    onRequestDownload(email.trim() || undefined);
  };

  return (
    <div className={className}>
      <p className="c-widget__headline c-widget__headline--larger u-normal-case u-mb-spacer-base-small">
        Share Your Results
      </p>
      <div className="c-widget__desc">
        <p>
          To share the results of this calculation, enter an email address and click Send, or share the project with a
          link by clicking Copy URL.
        </p>
      </div>
      <form className="c-form--float-labels fl-form u-flex u-items-end u-mb-spacer-base" onSubmit={handleSend}>
        <div className="c-form-element c-form-element--style-fill u-flex-grow u-mb-0">
          <div className="c-form-element__field u-mb-0">
            <div
              className={classNames('fl-wrap fl-wrap-input', {
                'fl-is-active': Boolean(email.length),
                'fl-has-focus': isFocused,
              })}
            >
              <label htmlFor="tempcontrol-share-email" className="c-form-label">
                Email Address
              </label>
              <input
                id="tempcontrol-share-email"
                type="email"
                className="fl-input"
                placeholder="Email Address"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
              />
            </div>
          </div>
        </div>
        <div className="u-ml-spacer u-flex-shrink-0">
          <button type="submit" className="c-button">
            Send
          </button>
        </div>
      </form>
      <hr className="u-hidden u-block@md" />
      <div className="c-widget__desc c-widget__desc--larger u-flex u-flex-wrap u-items-center u-mb-spacer-base-small">
        <div className="u-flex-grow u-mr-spacer-base-small">
          <p className="u-font-medium">... or copy the link</p>
        </div>
        <div className="u-mb-spacer-base-small">
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" className="c-link-cta" onClick={handleCopyUrl}>
            <span>Copy URL</span>
            <Icon name="link" className="o-svg-icon" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default ShareResultsWidget;
